import type { MermaidElementModel } from '@blocksuite/affine-model';
import { sanitizeSvg } from '@blocksuite/affine-shared/utils';

import { getMermaidRenderStateSignal } from './renderer/state.js';

export type MermaidPngScale = 1 | 2;

export class MermaidInteropError extends Error {
  constructor(
    readonly code:
      | 'clipboard_unavailable'
      | 'clipboard_write_failed'
      | 'render_unavailable'
      | 'invalid_svg'
      | 'png_export_failed',
    message: string
  ) {
    super(message);
    this.name = 'MermaidInteropError';
  }
}

export function getExportableMermaidSvg(model: MermaidElementModel): string {
  const state = getMermaidRenderStateSignal(model).value;
  if (!state.svg) {
    throw new MermaidInteropError(
      'render_unavailable',
      'Mermaid diagram has no validated rendered SVG yet.'
    );
  }

  const sanitized = sanitizeSvg(state.svg);
  if (!sanitized) {
    throw new MermaidInteropError(
      'invalid_svg',
      'Mermaid rendered SVG could not be sanitized.'
    );
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'image/svg+xml');
  const root = doc.documentElement;
  if (root.tagName.toLowerCase() !== 'svg') {
    throw new MermaidInteropError('invalid_svg', 'Invalid Mermaid SVG root.');
  }

  root.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  const intrinsic = state.intrinsicSize;
  if (!root.hasAttribute('viewBox') && intrinsic) {
    root.setAttribute(
      'viewBox',
      '0 0 ' + intrinsic.width + ' ' + intrinsic.height
    );
  }
  if (!root.hasAttribute('width') && intrinsic) {
    root.setAttribute('width', String(intrinsic.width));
  }
  if (!root.hasAttribute('height') && intrinsic) {
    root.setAttribute('height', String(intrinsic.height));
  }

  const existingStyle = root.getAttribute('style')?.trim();
  const fontFallback = 'font-family:Arial,"Noto Sans","Segoe UI",sans-serif';
  if (!existingStyle?.includes('font-family')) {
    root.setAttribute(
      'style',
      existingStyle ? existingStyle + ';' + fontFallback : fontFallback
    );
  }

  return new XMLSerializer().serializeToString(root);
}

function fallbackCopyText(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.append(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } finally {
    textarea.remove();
  }
}

export async function copyMermaidText(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      if (fallbackCopyText(text)) return;
      throw new MermaidInteropError(
        'clipboard_write_failed',
        'Unable to write Mermaid content to clipboard.'
      );
    }
  }

  if (!fallbackCopyText(text)) {
    throw new MermaidInteropError(
      'clipboard_unavailable',
      'Clipboard API is unavailable.'
    );
  }
}

export function downloadMermaidText(
  filename: string,
  content: string,
  mimeType: string
): void {
  downloadBlob(filename, new Blob([content], { type: mimeType }));
}

export function resolveMermaidExportBackground(
  themeMode: MermaidElementModel['themeMode'],
  prefersDark: boolean
): string {
  if (themeMode === 'dark' || (themeMode === 'auto' && prefersDark)) {
    return '#1e1e1e';
  }
  return '#ffffff';
}

export async function exportMermaidPng(
  model: MermaidElementModel,
  scale: MermaidPngScale,
  prefersDark: boolean
): Promise<Blob> {
  const svg = getExportableMermaidSvg(model);
  const state = getMermaidRenderStateSignal(model).value;
  const intrinsic = state.intrinsicSize;
  if (!intrinsic) {
    throw new MermaidInteropError(
      'render_unavailable',
      'Mermaid diagram has no intrinsic render size.'
    );
  }

  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(intrinsic.width * scale));
    canvas.height = Math.max(1, Math.ceil(intrinsic.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new MermaidInteropError(
        'png_export_failed',
        'Canvas 2D context is unavailable.'
      );
    }

    ctx.fillStyle = resolveMermaidExportBackground(
      model.themeMode,
      prefersDark
    );
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const png = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/png')
    );
    if (!png) {
      throw new MermaidInteropError(
        'png_export_failed',
        'PNG encoder returned no data.'
      );
    }
    return png;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
